import { useEffect, useMemo, useState } from 'react';
import {
  IconBuildingBank,
  IconGavel,
  IconScale,
  IconShield,
  IconSparkles,
  IconTrash,
  IconUser,
  IconUserCheck,
  IconUserPlus,
} from '@tabler/icons-react';
import Btn from './ui/Btn';
import { api, ApiError } from '../lib/api';
import { toast } from '../stores/toaster';
import type { Partie, PdfMetadata } from '../lib/types';

/**
 * Sous-onglet « Parties » sous « Dossier » (ADR-025 v2, 2026-05-12).
 *
 * Recréé après l'abandon initial : la valeur ajoutée est l'extraction
 * automatique depuis une pièce de citation (Haiku 4.5) — l'avocat ne
 * resaisit plus rien, il marque juste son/ses client(s).
 *
 * Actions :
 *   - extraire depuis une pièce (sélecteur PDF)
 *   - ajouter manuellement (formulaire compact)
 *   - toggle « Mon client » sur chaque partie
 *   - éditer rôle / qualité / notes
 *   - supprimer
 */

const ROLE_OPTIONS: { value: Partie['role']; label: string }[] = [
  { value: 'prevenu', label: 'Prévenu / inculpé' },
  { value: 'partie_civile', label: 'Partie civile' },
  { value: 'victime', label: 'Victime' },
  { value: 'temoin', label: 'Témoin' },
  { value: 'expert', label: 'Expert' },
  { value: 'enqueteur', label: 'Enquêteur' },
  { value: 'autre', label: 'Autre' },
];

const ROLE_ICONS: Partial<Record<Partie['role'], typeof IconUser>> = {
  prevenu: IconShield,
  partie_civile: IconGavel,
  victime: IconScale,
  temoin: IconUser,
  expert: IconBuildingBank,
  enqueteur: IconShield,
};

type Props = {
  dossierId: string;
  pdfs: PdfMetadata[];
  parties: Partie[];
  onChange: () => void;
};

type ExtractReport = {
  report: {
    created_count: number;
    created: { id: string; nom: string; prenom: string | null; role: string }[];
    skipped_duplicates: { nom: string; prenom: string; role: string }[];
    skipped_non_persisted: { nom: string; prenom: string; role: string }[];
    juridiction: string | null;
    date_audience: string | null;
    chefs_accusation: string[];
  };
  parties: Partie[];
};

export default function PartiesTab({ dossierId, pdfs, parties, onChange }: Props) {
  const [extractOpen, setExtractOpen] = useState(false);
  const [selectedPdfId, setSelectedPdfId] = useState<string>('');
  const [extracting, setExtracting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState<{
    nom: string;
    prenom: string;
    role: Partie['role'];
    qualite: string;
    is_client: boolean;
  }>({ nom: '', prenom: '', role: 'partie_civile', qualite: '', is_client: false });
  const [busyId, setBusyId] = useState<string | null>(null);

  // Heuristique : pièce qui ressemble à une citation (filename match).
  const citationCandidates = useMemo(() => {
    const re = /citation|conv(?:o[ck]ation)?|assignation|comparaitre|comparaître/i;
    return pdfs.filter((p) => re.test(p.filename_original));
  }, [pdfs]);

  useEffect(() => {
    if (extractOpen && !selectedPdfId && citationCandidates[0]) {
      setSelectedPdfId(citationCandidates[0].id);
    }
  }, [extractOpen, selectedPdfId, citationCandidates]);

  const runExtract = async () => {
    if (!selectedPdfId) {
      toast.error('Sélectionne une pièce.');
      return;
    }
    setExtracting(true);
    try {
      const res = await api.post<ExtractReport>(`/dossiers/${dossierId}/parties/extract`, {
        pdf_file_id: selectedPdfId,
      });
      const r = res.report;
      const msgParts: string[] = [];
      if (r.created_count > 0) msgParts.push(`${r.created_count} partie(s) créée(s)`);
      if (r.skipped_duplicates.length > 0)
        msgParts.push(`${r.skipped_duplicates.length} doublon(s) ignoré(s)`);
      if (r.created_count === 0 && r.skipped_duplicates.length === 0) {
        msgParts.push('Aucune partie nouvelle trouvée.');
      }
      toast.success(msgParts.join(' · '));
      setExtractOpen(false);
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setExtracting(false);
    }
  };

  const runAdd = async () => {
    if (!addDraft.nom.trim()) {
      toast.error('Le nom est requis.');
      return;
    }
    try {
      await api.post(`/dossiers/${dossierId}/parties`, {
        nom: addDraft.nom.trim(),
        prenom: addDraft.prenom.trim() || null,
        role: addDraft.role,
        qualite: addDraft.qualite.trim() || null,
        is_client: addDraft.is_client,
      });
      toast.success(`${addDraft.nom} ajouté(e).`);
      setAddDraft({ nom: '', prenom: '', role: 'partie_civile', qualite: '', is_client: false });
      setAddOpen(false);
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const toggleClient = async (p: Partie) => {
    setBusyId(p.id);
    try {
      await api.patch(`/parties/${p.id}`, { is_client: !p.is_client });
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusyId(null);
    }
  };

  const removePartie = async (p: Partie) => {
    if (!window.confirm(`Supprimer ${p.prenom ?? ''} ${p.nom} ?`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/parties/${p.id}`);
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusyId(null);
    }
  };

  const clients = parties.filter((p) => p.is_client);
  const others = parties.filter((p) => !p.is_client);

  return (
    <div className="space-y-4">
      <header className="rounded-md border border-rule bg-paper-elevated p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-[15px] font-medium text-text-primary">
              Parties au dossier
            </h2>
            <p className="mt-1 text-[12px] text-text-secondary">
              Liste des personnes impliquées dans la procédure pénale. Coche{' '}
              <strong className="text-text-primary">Mon client</strong> sur celle(s) qui sont
              défendue(s) par le cabinet — utilisé par les actes de procédure et les exports.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn variant="secondary" size="sm" onClick={() => setAddOpen((v) => !v)}>
              <IconUserPlus size={14} stroke={1.6} /> Ajouter
            </Btn>
            <Btn variant="primary" size="sm" onClick={() => setExtractOpen((v) => !v)}>
              <IconSparkles size={14} stroke={1.6} /> Extraire depuis une pièce
            </Btn>
          </div>
        </div>
      </header>

      {extractOpen && (
        <div className="rounded-md border border-rule bg-paper-elevated p-3 text-[12.5px]">
          <p className="mb-2 text-text-secondary">
            Sélectionne la pièce contenant la citation, le PV initial ou tout document listant les
            parties. Haiku 4.5 parse le texte OCR et crée les parties manquantes.
            {citationCandidates.length > 0 && (
              <span className="block text-[11px] text-text-tertiary mt-1">
                Détecté automatiquement : {citationCandidates.length} pièce(s) avec « citation », «
                convocation » ou « assignation » dans le nom.
              </span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedPdfId}
              onChange={(e) => setSelectedPdfId(e.target.value)}
              className="flex-1 min-w-0 rounded-md border border-rule-strong bg-paper-card px-3 py-1.5 text-[12.5px]"
            >
              <option value="">— Choisir une pièce —</option>
              {citationCandidates.length > 0 && (
                <optgroup label="Probable citation">
                  {citationCandidates.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.filename_original}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Toutes les pièces">
                {pdfs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.filename_original}
                  </option>
                ))}
              </optgroup>
            </select>
            <Btn
              variant="primary"
              size="sm"
              onClick={() => void runExtract()}
              disabled={extracting}
            >
              {extracting ? 'Extraction…' : "Lancer l'extraction"}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setExtractOpen(false)}>
              Annuler
            </Btn>
          </div>
        </div>
      )}

      {addOpen && (
        <div className="rounded-md border border-rule bg-paper-elevated p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="text-[11px] text-text-secondary">
              Nom *
              <input
                type="text"
                value={addDraft.nom}
                onChange={(e) => setAddDraft((d) => ({ ...d, nom: e.target.value }))}
                className="mt-0.5 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1 text-[13px] text-text-primary"
                placeholder="DUPONT"
              />
            </label>
            <label className="text-[11px] text-text-secondary">
              Prénom
              <input
                type="text"
                value={addDraft.prenom}
                onChange={(e) => setAddDraft((d) => ({ ...d, prenom: e.target.value }))}
                className="mt-0.5 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1 text-[13px] text-text-primary"
                placeholder="Jean"
              />
            </label>
            <label className="text-[11px] text-text-secondary">
              Rôle
              <select
                value={addDraft.role}
                onChange={(e) =>
                  setAddDraft((d) => ({ ...d, role: e.target.value as Partie['role'] }))
                }
                className="mt-0.5 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1 text-[13px]"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[11px] text-text-secondary">
              Qualité / précision
              <input
                type="text"
                value={addDraft.qualite}
                onChange={(e) => setAddDraft((d) => ({ ...d, qualite: e.target.value }))}
                className="mt-0.5 w-full rounded-md border border-rule-strong bg-paper-card px-2 py-1 text-[13px]"
                placeholder="domicilié à Liège"
              />
            </label>
          </div>
          <label className="mt-2 flex items-center gap-1.5 text-[12px] text-text-secondary">
            <input
              type="checkbox"
              checked={addDraft.is_client}
              onChange={(e) => setAddDraft((d) => ({ ...d, is_client: e.target.checked }))}
            />
            Mon client
          </label>
          <div className="mt-2 flex gap-2">
            <Btn variant="primary" size="sm" onClick={() => void runAdd()}>
              Ajouter
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setAddOpen(false)}>
              Annuler
            </Btn>
          </div>
        </div>
      )}

      {parties.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule bg-paper-subtle p-6 text-center text-[13px] text-text-tertiary">
          Aucune partie pour le moment. Lance « Extraire depuis une pièce » sur la citation, ou
          ajoute manuellement.
        </p>
      ) : (
        <>
          {clients.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-secondary">
                Nos client·e·s
              </h3>
              <ul className="space-y-1.5">
                {clients.map((p) => (
                  <PartieRow
                    key={p.id}
                    partie={p}
                    busy={busyId === p.id}
                    onToggleClient={() => void toggleClient(p)}
                    onRemove={() => void removePartie(p)}
                  />
                ))}
              </ul>
            </section>
          )}
          {others.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-secondary">
                Autres parties ({others.length})
              </h3>
              <ul className="space-y-1.5">
                {others.map((p) => (
                  <PartieRow
                    key={p.id}
                    partie={p}
                    busy={busyId === p.id}
                    onToggleClient={() => void toggleClient(p)}
                    onRemove={() => void removePartie(p)}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function PartieRow({
  partie: p,
  busy,
  onToggleClient,
  onRemove,
}: {
  partie: Partie;
  busy: boolean;
  onToggleClient: () => void;
  onRemove: () => void;
}) {
  const Icon = ROLE_ICONS[p.role] ?? IconUser;
  const fullName = `${p.prenom ?? ''} ${p.nom}`.trim();
  return (
    <li
      className={`flex items-center gap-3 rounded-md border p-2.5 text-[13px] ${
        p.is_client ? 'border-accent-light bg-accent-lighter' : 'border-rule bg-paper-elevated'
      }`}
    >
      <Icon size={18} stroke={1.6} className="shrink-0 text-text-secondary" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-text-primary">
          {fullName || '<sans nom>'}
          <span className="ml-2 rounded-pill bg-paper-subtle px-2 py-[1px] text-[10px] font-normal text-text-secondary">
            {p.role}
          </span>
        </div>
        {p.qualite && <div className="text-[11px] text-text-tertiary">{p.qualite}</div>}
      </div>
      <button
        type="button"
        onClick={onToggleClient}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition ${
          p.is_client
            ? 'bg-accent-light text-accent-text hover:opacity-90'
            : 'text-text-secondary hover:bg-paper-subtle hover:text-text-primary'
        }`}
        title={
          p.is_client ? 'Cliquer pour retirer du tag « mon client »' : 'Marquer comme mon client'
        }
      >
        <IconUserCheck size={14} stroke={1.6} />
        {p.is_client ? 'Mon client' : 'Marquer'}
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="text-text-tertiary hover:text-[#991b1b]"
        title="Supprimer"
      >
        <IconTrash size={14} stroke={1.6} />
      </button>
    </li>
  );
}
